import { useEffect, useState } from 'react';
import { FETCH_DIVISIONS } from '../pages/dataInputs/queries';
import { useQuery } from '@apollo/client';
import { FIND_ALL_LOCATIONS } from '../pages/bcp/queries';
import { FindAllLocations } from '../interfaces/bcp';
import { useUserContext } from '../context/PermissionsContext';
import { getFilteredTags } from '../utils/getFilteredTags';

interface Filters {
  order: number;
  name: string;
  label: string;
  childen: any;
}

const useFilters = (showFilters: string[]) => {
  const [filters, setFilters] = useState<Filters[]>([]);

  const { data: divisionsData } = useQuery(FETCH_DIVISIONS);

  const { isDivision, isLocation, selectedTags } = useUserContext();

  const filteredDivisions = getFilteredTags({
    data: divisionsData?.findAllDivisions,
    condition: isDivision,
    selectedTags,
  });

  const { data: locationsData } =
    useQuery<FindAllLocations>(FIND_ALL_LOCATIONS);

  const filteredLocations = getFilteredTags({
    data: locationsData?.findAllLocations,
    condition: isLocation,
    selectedTags,
  });

  /**
   * Sorts an array of objects by a specified key.
   *
   * @param {T[]} array - The array of objects to sort.
   * @param {keyof T} key - The key to sort by.
   * @param {boolean} [ascending=true] - Whether to sort in ascending order. Default is true.
   * @returns {T[]} - The sorted array.
   */
  const sortByKey = <T>(
    array: T[],
    key: keyof T,
    ascending: boolean = true,
  ): T[] => {
    return array.sort((a, b) => {
      if (a[key] < b[key]) {
        return ascending ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return ascending ? 1 : -1;
      }
      return 0;
    });
  };

  useEffect(() => {
    if (
      !divisionsData ||
      !filteredDivisions ||
      filters.some(({ name }) => name === 'division')
    ) {
      return;
    }

    const divisions = filteredDivisions?.map((division: any) => ({
      label: division.name,
      value: division.uuid,
    }));

    const newFilters: any = [
      ...filters,
      {
        order: 1,
        name: 'division',
        label: 'Division Filter',
        children: divisions,
      },
    ];

    setFilters(newFilters);
  }, [divisionsData]);

  useEffect(() => {
    if (
      !locationsData ||
      !filteredLocations ||
      filters.some(({ name }) => name === 'location')
    ) {
      return;
    }

    const locations = filteredLocations?.map((location: any) => ({
      label: location.locationName,
      value: location.uuid,
    }));

    const newFilters: any = [
      ...filters,
      {
        order: 2,
        name: 'location',
        label: 'Locations Filter',
        children: locations,
      },
    ];

    setFilters(newFilters);
  }, [locationsData]);

  return {
    filters: sortByKey(
      filters.filter((filterGroup) => showFilters.includes(filterGroup.name)),
      'order',
    ),
  };
};

export default useFilters;
